import { PaymentOutput } from "@/types/payment.types";
import { formatShortId } from "@/lib/shortId";

/**
 * Number of trailing id characters used as the invoice number in the export.
 *
 * Long enough to be practically unique across invoices, short enough to read.
 * The "#" prefix added by formatShortId also stops Excel from treating hex values
 * such as "1234567E89" as scientific notation or dropping leading zeros.
 */
const INVOICE_NUMBER_LENGTH = 10;

/**
 * Formats a payment mode enum value into a human-readable label.
 *
 * e.g. "BANK_TRANSFER" → "Bank Transfer", "APPLE_PAY" → "Apple Pay"
 */
function formatPaymentMode(mode: string): string {
  const modeMap: Record<string, string> = {
    CASH: "Cash",
    BANK_TRANSFER: "Bank Transfer",
    POS: "POS",
    CREDIT_CARD: "Credit Card",
    DEBIT_CARD: "Debit Card",
    APPLE_PAY: "Apple Pay",
    G_PAY: "Google Pay",
    WALLET: "Wallet",
  };
  return modeMap[mode] || mode.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Formats a payment status enum value into a human-readable label.
 *
 * Maps to match the client's expected output:
 *   COMPLETED → "Paid", PENDING → "Pending", CANCELLED → "Cancelled"
 */
function formatPaymentStatus(status: string): string {
  const statusMap: Record<string, string> = {
    COMPLETED: "Paid",
    PENDING: "Pending",
    CANCELLED: "Cancelled",
    FAILED: "Failed",
  };
  return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

/**
 * Formats a service type into the "Package Type" column value.
 *
 *   "one-time" → "One Time", "subscription" → "Subscription"
 */
function formatPackageType(serviceType?: string): string {
  if (serviceType === "one-time") return "One Time";
  if (serviceType === "subscription") return "Subscription";
  return serviceType || "-";
}

/**
 * Formats a date value into YYYY-MM-DD for the CSV.
 */
function formatDateForCSV(dateValue?: string | Date | null): string {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "-";
  return date.toISOString().split("T")[0];
}

/**
 * Escapes a CSV field value. Wraps in quotes if it contains commas,
 * double-quotes, or newlines.
 */
function escapeCSVField(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n") || value.includes("\r")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Builds a CSV string from an array of payment records, following the
 * client-specified format:
 *
 * Sequence | Date | Invoice Number | Customer Name | Package Type |
 * Service Type | Amount | Payment Status | Payment Method
 */
function buildCSVContent(payments: PaymentOutput[]): string {
  const headers = [
    "Sequence",
    "Date",
    "Invoice Number",
    "Customer Name",
    "Package Type",
    "Service Type",
    "Amount",
    "Payment Status",
    "Payment Method",
  ];

  const rows = payments.map((payment, index) => {
    const date = payment.appointmentScheduledAt
      ? formatDateForCSV(payment.appointmentScheduledAt)
      : formatDateForCSV(payment.date);

    return [
      String(index + 1),
      date,
      formatShortId(payment.id, INVOICE_NUMBER_LENGTH),
      payment.customer?.name || "N/A",
      formatPackageType(payment.serviceType),
      payment.serviceName || "-",
      String(payment.totalAmount),
      formatPaymentStatus(payment.status),
      formatPaymentMode(payment.mode),
    ].map(escapeCSVField);
  });

  return [headers.join(","), ...rows.map((row) => row.join(","))].join("\r\n");
}

/**
 * Triggers a browser file download for the given content.
 */
function downloadFile(content: string, filename: string): void {
  // BOM for UTF-8 — ensures Excel opens the file with correct encoding
  const bom = "\uFEFF";
  const blob = new Blob([bom + content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the object URL after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Exports billing/payment data as a CSV file download.
 *
 * The CSV follows the client-specified format with columns:
 * Sequence, Date, Invoice Number, Customer Name, Package Type,
 * Service Type, Amount, Payment Status, Payment Method
 */
export function exportBillingCSV(payments: PaymentOutput[]): void {
  const csvContent = buildCSVContent(payments);
  const today = new Date().toISOString().split("T")[0];
  const filename = `Billing_and_Payment_Details_${today}.csv`;
  downloadFile(csvContent, filename);
}
